import {
  ACCESS_GROUPS,
  type AccessGroupRule,
  hasAppAudience,
  hasGroupName,
  matchesAccessGroupRule,
} from "../../access-control/models/access-groups.models";
import {
  ADMIN_FEATURE_KEYS,
  FEATURE_KEYS,
  EffectiveAccessControl,
} from "../../access-control/models/role-feature.models";
import { hasAnyFeatureAccess } from "../../access-control/utils/feature-access";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { log, logV } from "../../shared/services/debug-logger";
import { getDxoUserAndToken } from "../services/getDxoUserToken";
import { useAuthContext } from "../state/AuthContext";
import { datadogRum } from "@datadog/browser-rum";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";

interface Props {
  group?: string | string[];
  featureKey?: string;
  featureKeys?: readonly string[];
  requiredGroups?: AccessGroupRule;
  accessRules?: readonly AccessRule[];
  redirectTo?: string;
}

interface AccessRule {
  featureKey?: string;
  featureKeys?: readonly string[];
  groups?: AccessGroupRule;
}

// Used to segregate access between user groups and backend-resolved feature keys.
export const AccessControl: React.FC<Props> = (props) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [ready, setReady] = useState(false);
  const authContext = useAuthContext();
  logV("AccessControl - BUILD", { ready, authenticated: authState?.isAuthenticated ?? false });

  useEffect(() => {
    async function handle() {
      if (ready) {
        return;
      }

      if (!authState) {
        return;
      }

      if (authState.isAuthenticated) {
        if (!authContext.state.user?.accessTokenRaw || !authContext.state.user.accessControl) {
          log("AccessControl - handle - resolve access");
          const dxoUserAndToken = await getDxoUserAndToken();
          authContext.dispatch({ type: "SET_USER", payload: dxoUserAndToken });
        }

        datadogRum.setGlobalContextProperty("environment", process.env.REACT_APP_RUM_ENV);
        setReady(true);
        log("AccessControl - handle - ready");
        return;
      }

      log("AccessControl - handle - sign in redirect");
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri(originalUri);

      // Sign In
      oktaAuth.signInWithRedirect();
    }

    handle();
  }, [oktaAuth, authState, ready, authContext]);

  // Render Content
  if (ready) {
    const groups = authContext.state.user?.accessToken?.groups ?? [];
    const requestedFeatureKeys = props.featureKeys ?? (props.featureKey ? [props.featureKey] : []);
    const requiredGroups = Array.isArray(props.group) ? props.group : props.group ? [props.group] : [];
    const hasGroupAccess = requiredGroups.some((group) => hasGroupName(groups, group));
    const isInternal = hasGroupName(groups, ACCESS_GROUPS.InternalUsers);
    const hasAudience = hasAppAudience(groups);
    const currentRule =
      requestedFeatureKeys.length > 0 || props.requiredGroups
        ? [{ featureKeys: requestedFeatureKeys, groups: props.requiredGroups }]
        : [];
    const hasRuleAccess = [...currentRule, ...(props.accessRules ?? [])].some((rule) =>
      matchesAccessRule(groups, authContext.state.user?.accessControl, rule),
    );

    if (hasAudience && (hasGroupAccess || hasRuleAccess)) {
      return <Outlet />;
    } else {
      // This is a rather simple approach to the redirect question.
      // That's all we need for now to deal with internal and external users.
      const redirectPage =
        props.redirectTo ?? getDefaultRedirect(groups, authContext.state.user?.accessControl, isInternal);
      logV("AccessControl - getDefaultRedirect", { hasAudience, redirect: redirectPage });
      return <Navigate to={redirectPage} />;
    }
  }

  return <LoadingWrapper isLoading={true} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg" />;
};

const getDefaultRedirect = (
  groups: string[],
  accessControl: EffectiveAccessControl | null | undefined,
  isInternal: boolean,
) => {
  const isExternal = hasGroupName(groups, ACCESS_GROUPS.ExternalUsers);

  if (!hasAppAudience(groups)) {
    return "/errors/403";
  }

  if (isInternal && hasAnyFeatureAccess(accessControl, [FEATURE_KEYS.CORE_MY_VESSELS])) {
    return "/home";
  }

  if (isInternal && hasAnyFeatureAccess(accessControl, [FEATURE_KEYS.CORE_SEARCH_VESSELS])) {
    return "/vessels";
  }

  if (isInternal && hasAnyFeatureAccess(accessControl, [FEATURE_KEYS.HOLD_CLEANING_ACCESS])) {
    return "/hold-cleaning";
  }

  if (isInternal && hasAnyFeatureAccess(accessControl, [FEATURE_KEYS.CORE_CERTS])) {
    return "/certificates";
  }

  if (isInternal && hasAnyFeatureAccess(accessControl, [FEATURE_KEYS.REVIEW_PNL])) {
    return "/voyage/pnlList";
  }

  const hasBqTqAdminAccess =
    hasAnyFeatureAccess(accessControl, [FEATURE_KEYS.TQ_ADMIN]) &&
    (hasGroupName(groups, ACCESS_GROUPS.Admin) || hasGroupName(groups, ACCESS_GROUPS.VesselPerformance));

  if (hasAnyFeatureAccess(accessControl, ADMIN_FEATURE_KEYS) || hasBqTqAdminAccess) {
    return "/admin";
  }

  if (isExternal) {
    return "/landingPage";
  }

  return "/errors/403";
};

const matchesAccessRule = (
  groups: readonly string[],
  accessControl: EffectiveAccessControl | null | undefined,
  rule: AccessRule,
): boolean => {
  const featureKeys = rule.featureKeys ?? (rule.featureKey ? [rule.featureKey] : []);
  const hasFeatureAccess = featureKeys.length === 0 || hasAnyFeatureAccess(accessControl, featureKeys);
  return hasFeatureAccess && matchesAccessGroupRule(groups, rule.groups);
};
